import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import {
  adminListCertificateTemplates,
  adminGetActiveCertificateTemplate,
  adminUploadCertificateTemplate,
  adminActivateCertificateTemplate,
  adminPreviewActiveCertificateTemplate,
  adminPreviewCertificateTemplate,
  adminPreviewDraftCertificateTemplate,
} from "@/lib/apiClient";
import { Upload, Check, Loader2, ImageIcon, Eye } from "lucide-react";

/**
 * Admin → Certificate Templates CMS (scaffold).
 *
 * Admins can:
 *   • upload a PNG/JPG/PDF background (≤ 5 MB),
 *   • set the certificate title / subtitle / accent colour,
 *   • promote any uploaded template to "active".
 * The active template is referenced by the certificate-rendering
 * pipeline when a learner downloads their PDF certificate.
 */
export default function AdminCertificateTemplates() {
  const [templates, setTemplates] = useState([]);
  const [active, setActive] = useState(null);
  const [meta, setMeta] = useState({
    name: "Default",
    accent_color: "#064E3B",
    title: "Quran Fluency Certificate",
    subtitle: "This certifies that",
    show_qr: true,
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    try {
      const [list, act] = await Promise.all([
        adminListCertificateTemplates(),
        adminGetActiveCertificateTemplate(),
      ]);
      setTemplates(list || []);
      setActive(act || null);
    } catch (_e) { /* not blocking */ }
  };

  useEffect(() => { load(); }, []);

  const onUpload = async () => {
    if (!file) {
      toast.error("Choose a background image / PDF first");
      return;
    }
    setBusy(true);
    try {
      await adminUploadCertificateTemplate(meta, file);
      toast.success("Template uploaded and set active");
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const onActivate = async (id) => {
    try {
      await adminActivateCertificateTemplate(id);
      toast.success("Template activated");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not activate");
    }
  };

  /**
   * Open a generated sample PDF in a new tab. We use object-URLs returned
   * from the apiClient (the response was fetched as a blob) so the JWT
   * auth header is honoured by axios instead of leaking via a plain
   * <a href> link.
   */
  const previewDraft = async () => {
    try {
      const url = await adminPreviewDraftCertificateTemplate(meta);
      window.open(url, "_blank", "noopener");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    }
  };

  const previewActive = async () => {
    try {
      const url = await adminPreviewActiveCertificateTemplate();
      window.open(url, "_blank", "noopener");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    }
  };

  const previewById = async (id) => {
    try {
      const url = await adminPreviewCertificateTemplate(id);
      window.open(url, "_blank", "noopener");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    }
  };

  return (
    <div data-testid="admin-cert-templates" className="space-y-8">
      <header>
        <div className="text-xs uppercase tracking-[0.22em] text-[#4B5563]">Admin</div>
        <h1 className="font-heading text-3xl font-semibold mt-2 text-[#1F2937]">Certificate Templates</h1>
        <p className="text-[#4B5563] mt-2 max-w-xl">
          Upload your own certificate background and configure title / accent colour. Only one
          template is active at a time — that's what learners see when they download their
          verified certificate after AI + teacher review.
        </p>
      </header>

      {/* Upload form */}
      <section className="rounded-2xl bg-white border border-[#E7E0D2] p-6">
        <h2 className="font-heading text-lg font-medium text-[#1F2937]">Upload new template</h2>
        <div className="mt-5 grid sm:grid-cols-2 gap-4">
          <Field label="Template name">
            <input
              data-testid="cert-tpl-name"
              type="text" value={meta.name}
              onChange={(e) => setMeta({ ...meta, name: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm" />
          </Field>
          <Field label="Accent colour">
            <div className="flex items-center gap-2">
              <input
                data-testid="cert-tpl-accent"
                type="color" value={meta.accent_color}
                onChange={(e) => setMeta({ ...meta, accent_color: e.target.value })}
                className="h-10 w-12 rounded border border-[#E7E0D2] bg-white" />
              <input
                type="text" value={meta.accent_color}
                onChange={(e) => setMeta({ ...meta, accent_color: e.target.value })}
                className="flex-1 px-3 py-2 rounded-lg border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm font-mono" />
            </div>
          </Field>
          <Field label="Title">
            <input
              data-testid="cert-tpl-title"
              type="text" value={meta.title}
              onChange={(e) => setMeta({ ...meta, title: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm" />
          </Field>
          <Field label="Subtitle">
            <input
              data-testid="cert-tpl-subtitle"
              type="text" value={meta.subtitle}
              onChange={(e) => setMeta({ ...meta, subtitle: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm" />
          </Field>
          <Field label="Background (PNG / JPG / PDF · ≤ 5 MB)">
            <input
              ref={inputRef}
              data-testid="cert-tpl-file"
              type="file" accept="image/png,image/jpeg,image/jpg,application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full text-sm" />
          </Field>
          <Field label="Show verification QR code">
            <label className="inline-flex items-center gap-2 text-sm text-[#1F2937]">
              <input type="checkbox" checked={meta.show_qr}
                onChange={(e) => setMeta({ ...meta, show_qr: e.target.checked })} />
              Include QR code in PDF
            </label>
          </Field>
        </div>
        <div className="mt-6 flex items-center gap-3 flex-wrap">
          <button
            data-testid="cert-tpl-upload-btn"
            onClick={onUpload} disabled={busy || !file}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#064E3B] hover:bg-[#047857] disabled:opacity-30 text-white font-medium text-sm transition">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Upload & activate
          </button>
          <button
            data-testid="cert-tpl-preview-draft"
            onClick={previewDraft}
            type="button"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-[#E7E0D2] hover:border-[#D97706] text-[#1F2937] font-medium text-sm transition">
            <Eye className="w-4 h-4" /> Preview before upload
          </button>
          {file && <span className="text-xs text-[#4B5563]">Selected: {file.name}</span>}
        </div>
      </section>

      {/* Active template */}
      <section className="rounded-2xl bg-white border border-[#E7E0D2] p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="font-heading text-lg font-medium text-[#1F2937]">Active template</h2>
          <button data-testid="cert-tpl-preview-active" onClick={previewActive}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#E7E0D2] hover:border-[#064E3B] text-sm text-[#1F2937]">
            <Eye className="w-4 h-4" /> Preview active
          </button>
        </div>
        {active ? (
          <div className="mt-4 flex items-center gap-3 text-sm text-[#1F2937]" data-testid="cert-tpl-active">
            <Check className="w-4 h-4 text-[#10B981]" />
            <span className="font-medium">{active.name}</span>
            <span className="text-xs text-[#4B5563]">accent {active.accent_color}</span>
          </div>
        ) : (
          <div className="mt-4 text-sm text-[#4B5563] inline-flex items-center gap-2">
            <ImageIcon className="w-4 h-4" /> No template uploaded yet — default theme is in use.
          </div>
        )}
      </section>

      {/* All templates */}
      <section className="rounded-2xl bg-white border border-[#E7E0D2] p-6">
        <h2 className="font-heading text-lg font-medium text-[#1F2937]">All templates</h2>
        {templates.length === 0 ? (
          <div className="mt-4 text-sm text-[#4B5563]">Nothing uploaded yet.</div>
        ) : (
          <ul className="mt-4 divide-y divide-[#E7E0D2]" data-testid="cert-tpl-list">
            {templates.map((tpl) => (
              <li key={tpl._id} className="py-3 flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-medium text-[#1F2937]">{tpl.name}</div>
                  <div className="text-xs text-[#4B5563]">{tpl.created_at}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button data-testid={`cert-tpl-preview-${tpl._id}`} onClick={() => previewById(tpl._id)}
                    className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-white border border-[#E7E0D2] hover:border-[#D97706] text-[#1F2937]">
                    <Eye className="w-3.5 h-3.5" /> Preview
                  </button>
                  {tpl.active ? (
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#ECFDF5] text-[#064E3B] border border-[#A7F3D0]">Active</span>
                  ) : (
                    <button onClick={() => onActivate(tpl._id)} className="text-xs px-3 py-1.5 rounded-full bg-white border border-[#E7E0D2] hover:border-[#064E3B] text-[#1F2937]">
                      Activate
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-[0.16em] text-[#4B5563] mb-1.5">{label}</div>
      {children}
    </label>
  );
}
